import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import { Alert, Stack } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { useContentSettings, useUpdateContentSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import { CONTENT_LOCALE_OPTIONS } from '../options';
import {
  FeedbackSnackbar,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';

/** Storefront content configuration. */
export function ContentSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('content');
  const query = useContentSettings();
  const update = useUpdateContentSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('contentSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('contentSettings.title')}
      subtitle={t('contentSettings.subtitle')}
      icon={<ArticleRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('contentSettings.shopInfoSection.title')} icon={<StorefrontRoundedIcon />}>
              <Alert severity="info" variant="outlined">
                <Trans
                  t={t}
                  i18nKey="contentSettings.shopInfoSection.infoAlert"
                  components={{ strong: <strong /> }}
                >
                  These toggles control which sections appear on the customer-facing "About shop"
                  page in the Telegram Mini App. The Telegram contact handle itself is configured
                  in <strong>Settings → Telegram → Business profile</strong>; business hours and
                  other published page content are managed in <strong>Content → Pages</strong>.
                </Trans>
              </Alert>
              <SwitchField
                label={t('contentSettings.shopInfoSection.shopInfoPageLabel')}
                description={t('contentSettings.shopInfoSection.shopInfoPageDescription')}
                checked={draft.shopInfoEnabled}
                onChange={(value) => form.setField('shopInfoEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.shopInfoSection.contactDetailsBlockLabel')}
                description={t('contentSettings.shopInfoSection.contactDetailsBlockDescription')}
                checked={draft.contactDetailsBlockEnabled}
                onChange={(value) => form.setField('contactDetailsBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.shopInfoSection.paymentMethodsBlockLabel')}
                checked={draft.paymentMethodsBlockEnabled}
                onChange={(value) => form.setField('paymentMethodsBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.shopInfoSection.deliveryOptionsBlockLabel')}
                checked={draft.deliveryOptionsBlockEnabled}
                onChange={(value) => form.setField('deliveryOptionsBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.shopInfoSection.returnPolicyBlockLabel')}
                checked={draft.returnPolicyBlockEnabled}
                onChange={(value) => form.setField('returnPolicyBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.shopInfoSection.legalSummaryBlockLabel')}
                checked={draft.legalSummaryBlockEnabled}
                onChange={(value) => form.setField('legalSummaryBlockEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.shopInfoSection.legalDocsRequiredLabel')}
                description={t('contentSettings.shopInfoSection.legalDocsRequiredDescription')}
                checked={draft.legalDocsRequired}
                onChange={(value) => form.setField('legalDocsRequired', value)}
                disabled={disabled}
              />
              <SelectField
                label={t('contentSettings.shopInfoSection.defaultLocaleLabel')}
                description={t('contentSettings.shopInfoSection.defaultLocaleDescription')}
                value={draft.defaultLocale ?? ''}
                options={CONTENT_LOCALE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('defaultLocale', value || null)}
                disabled={disabled}
                allowEmpty
              />
            </SettingsCard>

            <SettingsCard title={t('contentSettings.contentModulesSection.title')} icon={<ArticleRoundedIcon />}>
              <SwitchField
                label={t('contentSettings.contentModulesSection.storefrontPagesLabel')}
                checked={draft.storefrontPagesEnabled}
                onChange={(value) => form.setField('storefrontPagesEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.contentModulesSection.legalContentLabel')}
                checked={draft.legalContentEnabled}
                onChange={(value) => form.setField('legalContentEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.contentModulesSection.bannersLabel')}
                checked={draft.bannersEnabled}
                onChange={(value) => form.setField('bannersEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('contentSettings.contentModulesSection.landingPagesLabel')}
                checked={draft.landingPagesEnabled}
                onChange={(value) => form.setField('landingPagesEnabled', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('contentSettings.contentModulesSection.publishingRuleLabel')}
                value={draft.publishingRule}
                onChange={(value) => form.setField('publishingRule', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('contentSettings.seoSection.title')} icon={<TravelExploreRoundedIcon />}>
              <TextInputField
                label={t('contentSettings.seoSection.defaultTitleLabel')}
                value={draft.seoMetadataDefaults?.title ?? ''}
                onChange={(value) =>
                  form.setField('seoMetadataDefaults', {
                    ...draft.seoMetadataDefaults,
                    title: value || null,
                  })
                }
                disabled={disabled}
              />
              <TextInputField
                label={t('contentSettings.seoSection.defaultDescriptionLabel')}
                value={draft.seoMetadataDefaults?.description ?? ''}
                onChange={(value) =>
                  form.setField('seoMetadataDefaults', {
                    ...draft.seoMetadataDefaults,
                    description: value || null,
                  })
                }
                disabled={disabled}
                multiline
                rows={2}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
